import React from "react";
import { cn } from "../../lib/cn";

/**
 * A thin set of table primitives. Deliberately not a data-grid: the admin
 * screens show short, already-sorted lists, so this only carries the shared
 * hairlines, alignment and hover behaviour.
 *
 * The wrapper scrolls horizontally on its own so a wide table never makes the
 * page itself scroll sideways.
 */

export function Table({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn("w-full border-collapse text-left text-[13px]", className)}>
        {children}
      </table>
    </div>
  );
}

export function THead({ children }: { children: React.ReactNode }) {
  return (
    <thead className="bg-surface-sunken border-line border-y">{children}</thead>
  );
}

export function TH({
  children,
  className,
  ...rest
}: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        "text-ink-muted px-4 py-2.5 text-xs font-semibold tracking-wide whitespace-nowrap uppercase",
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export function TBody({ children }: { children: React.ReactNode }) {
  return <tbody>{children}</tbody>;
}

export function TR({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn(
        "border-line hover:bg-surface-hover border-b last:border-b-0",
        "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

export function TD({
  children,
  className,
  ...rest
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn("text-ink px-4 py-2.5 align-middle", className)} {...rest}>
      {children}
    </td>
  );
}

export function TableEmpty({
  colSpan,
  children,
}: {
  colSpan: number;
  children: React.ReactNode;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="text-ink-subtle px-4 py-10 text-center text-[13px]">
        {children}
      </td>
    </tr>
  );
}
